import { useCallback, useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import * as Sentry from '@sentry/react'

import { hasOpenOverlay, subscribeToOverlays } from '@helpers/overlayStack'
import { markShown, subscribeToReviewPromptDue } from '@msteams/api/reviewPrompt'

/** Pause between the screen going quiet and the prompt opening, so it never lands mid-transition. */
export const SETTLE_DELAY_MS = 1000

/**
 * Decides when the Teams review prompt opens. Mounted only by the Teams app,
 * so the web app never shows the prompt.
 *
 * The backend owns the rules (second approval in Teams, 7 days after the last
 * prompt, at most three prompts, never after a rating) and counts approvals
 * itself: an approve response carries `X-Review-Prompt: due` when the prompt
 * should open. Here we only pick the moment:
 * - never during an approval: wait until no popup or side panel is open and
 *   no mutation is in flight;
 * - never after an error: a failed mutation drops a prompt that is waiting;
 * - once per session: after it has opened, later "due" answers are ignored.
 */
const useReviewPromptTrigger = () => {
  const queryClient = useQueryClient()
  const [isOpen, setIsOpen] = useState(false)
  const isDue = useRef(false)
  const hasOpened = useRef(false)
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const isQuiet = useCallback(() => !hasOpenOverlay() && queryClient.isMutating() === 0, [queryClient])

  const cancelTimer = useCallback(() => {
    clearTimeout(timer.current)
    timer.current = undefined
  }, [])

  const openIfQuiet = useCallback(() => {
    if (!isDue.current || hasOpened.current) return
    if (!isQuiet()) {
      // Something opened during the settle delay: start over once it closes.
      cancelTimer()
      return
    }
    if (timer.current) return
    timer.current = setTimeout(() => {
      timer.current = undefined
      if (!isDue.current || !isQuiet()) return
      isDue.current = false
      hasOpened.current = true
      setIsOpen(true)
      markShown().catch((err) => Sentry.captureException(err))
    }, SETTLE_DELAY_MS)
  }, [isQuiet, cancelTimer])

  useEffect(() => {
    const unsubscribeDue = subscribeToReviewPromptDue(() => {
      isDue.current = true
      openIfQuiet()
    })
    const unsubscribeMutations = queryClient.getMutationCache().subscribe((event) => {
      if (event.type !== 'updated') return
      if (event.action.type === 'error') {
        isDue.current = false
        cancelTimer()
        return
      }
      openIfQuiet()
    })
    const unsubscribeOverlays = subscribeToOverlays(openIfQuiet)

    return () => {
      unsubscribeDue()
      unsubscribeMutations()
      unsubscribeOverlays()
      cancelTimer()
    }
  }, [queryClient, openIfQuiet, cancelTimer])

  const close = useCallback(() => setIsOpen(false), [])

  return { isOpen, close }
}

export default useReviewPromptTrigger
